<script>
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { apiFolders } from '@modules/aawz-intelligence-v2/api/apiFolders'
import {
  CONTEXT_TODAS_AS_PERSONAS,
  apiConversations,
} from '@modules/aawz-intelligence-v2/api/apiConversations'
import {
  excluirPasta,
  moverConversaParaPasta,
} from '@modules/aawz-intelligence-v2/utils/conversation-folder-actions'
import { readConversationMovePayload } from '@modules/aawz-intelligence-v2/utils/conversationMoveDrag'
import { filtrarPorTitulo, limitarRecentes } from '@modules/aawz-intelligence-v2/utils/agrupar-conversas'
import FolderItem from '@modules/aawz-intelligence-v2/components/folder-item/folder-item.vue'
import ConversationRow from '@modules/aawz-intelligence-v2/components/conversation-row/conversation-row.vue'

const CONVERSATIONS_LIMIT = 30
const DEBOUNCE_BUSCA_MS = 250

const DEFAULT_STORE_MODULE = 'aawz-intelligence-v2'

export default {
  name: 'IntelligenceV2Conversations',
  components: { FolderItem, ConversationRow },
  props: {
    // spec-003 §5 (decisão §3-A opção (a)): namespace do store parametrizável.
    // Default preserva o IA CRM byte a byte.
    storeModule: {
      type: String,
      default: DEFAULT_STORE_MODULE,
    },
    // ★ spec-001 (new-ux): o eixo de persona que o PAI usou para montar esta
    // coluna. Os dois recarregamentos daqui (mover conversa de pasta, apagar
    // pasta) refazem a busca por conta própria — sem repetir o mesmo critério,
    // a coluna volta calada para a persona DEFAULT do backend. Na home por IA
    // isso trocaria "todas as personas" por "só CRM" depois de arrastar uma
    // conversa para uma pasta. Vazio ⇒ as demais superfícies seguem byte a
    // byte como hoje.
    //
    // ★ STRING, não objeto (SH-2 da review): como objeto, ele era espalhado
    // por ÚLTIMO na chamada do `carregar` e um pai podia sobrescrever
    // `storeModule` — ou seja, redirecionar a ESCRITA para o slice de outro
    // produto, que é exatamente o isolamento que a spec-005/T003 criou. Uma
    // prop que só pode carregar o que deve não depende de ninguém lembrar da
    // ordem do spread.
    contextoListagem: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      expandedMap: {},
      // Conversas buscadas por pasta, sob demanda. O `byFolder` do store agrupa
      // só o que já está carregado (as N mais recentes) — uma pasta com
      // conversa antiga abria vazia mesmo com o chip contando certo.
      conversasDaPasta: {},
      pastasCarregando: {},
      moving: false,
      looseDragOver: false,
      // spec-007 R7 — busca client-side, teto de RECENTES e colapso por seção.
      busca: '',
      // O que o campo mostra AGORA. `busca` (o que de fato filtra) segue este
      // valor com atraso: sem isso cada tecla refiltrava a lista inteira e
      // reconstruía centenas de linhas, e a digitação engasgava.
      buscaDigitada: '',
      buscaTimer: null,
      recentesExpandido: false,
      secoes: { recentes: true, pastas: true },
    }
  },
  computed: {
    conversations() {
      return this.$store.getters[this.storeModule + '/conversations'] || []
    },
    // ★ Pastas também passam por `storeModule` (e não pelo namespace fixo
    // 'aawz-intelligence-v2'): o componente é reusado pela Central de IA sob
    // OUTRO namespace (spec-003 §5). Getter hardcoded leria o store do IA CRM
    // e a lista de pastas viria vazia — ou pior, com as pastas do outro produto.
    folders() {
      return this.$store.getters[this.storeModule + '/folders'] || []
    },
    foldersLoading() {
      return this.$store.getters[this.storeModule + '/foldersLoading']
    },
    byFolder() {
      return this.$store.getters[this.storeModule + '/conversationsByFolder'] || {}
    },
    // O que veio da busca por pasta VENCE o agrupamento das recentes: é a lista
    // completa daquela pasta, não o recorte que sobrou.
    conversasPorPasta() {
      const saida = { ...this.byFolder }
      const carregadas = this.conversasDaPasta
      Object.keys(carregadas).forEach(function(id) {
        saida[id] = carregadas[id]
      })
      return saida
    },
    looseConversations() {
      return this.$store.getters[this.storeModule + '/looseConversations'] || []
    },
    loading() {
      return this.$store.getters[this.storeModule + '/conversationsLoading']
    },
    selectedConversation() {
      return this.$store.getters[this.storeModule + '/selectedConversation']
    },
    selectedId() {
      const sel = this.selectedConversation
      return sel && sel.id ? sel.id : null
    },
    hasFolders() {
      return this.folders.length > 0
    },
    emBusca() {
      return this.busca.trim().length > 0
    },
    // Universo da busca: as conversas soltas MAIS as que já vieram por pasta.
    // Sem isto o gestor digitava o título de uma conversa que estava vendo numa
    // pasta aberta e a busca dizia "nenhuma conversa encontrada" — a lista
    // plana do store só tem as recentes, e as de pasta chegam por outra via.
    universoDaBusca() {
      const vistos = {}
      const todas = []
      const empilha = function(conv) {
        if (!conv || !conv.id || vistos[conv.id]) return
        vistos[conv.id] = true
        todas.push(conv)
      }
      ;(this.conversations || []).forEach(empilha)
      const porPasta = this.conversasDaPasta
      Object.keys(porPasta).forEach(function(id) {
        ;(porPasta[id] || []).forEach(empilha)
      })
      return todas
    },
    resultadosDaBusca() {
      return filtrarPorTitulo(this.universoDaBusca, this.busca)
    },
    recentes() {
      return limitarRecentes(this.conversations, this.recentesExpandido)
    },
    hasLoose() {
      return this.looseConversations.length > 0
    },
    initialLoading() {
      return (
        (this.loading || this.foldersLoading) &&
        !this.hasFolders &&
        !this.hasLoose
      )
    },
    isEmpty() {
      return (
        !this.loading &&
        !this.foldersLoading &&
        !this.hasFolders &&
        !this.hasLoose
      )
    },
    conversationsError() {
      return this.$store.getters[this.storeModule + '/conversationsError']
    },
    // O erro só toma a tela quando NÃO há lista para mostrar: falhar um refresh não
    // pode apagar as pastas/conversas que o usuário já estava vendo.
    mostraErro() {
      return this.conversationsError && !this.hasFolders && !this.hasLoose
    },
    mostraVazio() {
      return this.isEmpty && !this.conversationsError
    },
    // O que o recarregar pós-mutação (mover conversa, excluir pasta) repete.
    // Sem eixo do pai, o universo é o MESMO do `created` (todos os agentes):
    // as pastas já são carregadas assim, e o popup/painel listam com
    // `todosAgentes` — recarregar sem eixo cairia no default do backend
    // (`persona=CRM`) e o chip da pasta contaria um universo diferente da lista.
    criterioDeRecarga() {
      return {
        context: this.contextoListagem || CONTEXT_TODAS_AS_PERSONAS,
        storeModule: this.storeModule,
      }
    },
  },
  created() {
    // Carrega as pastas do profissional (setter puro na store compartilhada →
    // reflete no popup e na página cheia). As conversas são carregadas pelos
    // componentes-pai (view-chat / chat-ia-pop-up).
    // `catch` obrigatório: chamada disparada em hook de ciclo de vida, sem
    // ninguém para receber a rejeição. As pastas são acessório — sem elas a
    // sidebar ainda lista as conversas —, então falhar aqui degrada o modo
    // "Pastas" e não derruba a tela (nem vaza unhandled rejection).
    // `todosAgentes`: a lista deixou de filtrar por agente (o gestor não lembra
    // de qual agente é a conversa que procura), então o contador de cada pasta
    // tem que somar o mesmo universo — senão o chip volta a discordar do que a
    // pasta abre.
    apiFolders
      .carregar({ todosAgentes: true })
      .catch((erro) => console.warn('[IA] pastas não carregaram: ' + (erro && erro.message)))
  },
  // O timer da busca é estado de instância e não pode sobreviver ao
  // componente: o painel desmonta ao alternar para a conversa, e um
  // `setTimeout` pendente escreveria num `this` já destruído.
  beforeDestroy() {
    if (this.buscaTimer) clearTimeout(this.buscaTimer)
  },
  methods: {
    alternarSecao(nome) {
      this.secoes = { ...this.secoes, [nome]: !this.secoes[nome] }
    },
    alternarRecentes() {
      this.recentesExpandido = !this.recentesExpandido
    },
    onSelect(conversation) {
      this.$emit('select', conversation)
    },
    onRename(payload) {
      this.$emit('rename', payload)
    },
    confirmDelete(conversation) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: 'Remover conversa',
        text: 'Deseja remover esta conversa? Esta ação não pode ser desfeita.',
        buttons: [
          { text: 'Cancelar' },
          {
            text: 'Remover',
            action: () => {
              this.$emit('delete', conversation)
            },
          },
        ],
      })
    },
    openCreateFolder() {
      GlobalBus.$emit(MODAL.IA_FOLDER_CREATE)
    },
    openRenameFolder(folder) {
      GlobalBus.$emit(MODAL.IA_FOLDER_RENAME, { folder })
    },
    confirmDeleteFolder(folder) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: 'Excluir pasta',
        text:
          'Deseja excluir a pasta "' +
          (folder.name || '') +
          '"? As conversas serão mantidas e movidas para "sem pasta".',
        buttons: [
          { text: 'Cancelar' },
          {
            text: 'Excluir',
            action: () => this.deleteFolder(folder),
          },
        ],
      })
    },
    // Mover ou apagar conversa muda o conteúdo das pastas: o cache tem que cair,
    // senão a pasta segue mostrando o que ela tinha antes.
    invalidarCacheDePastas() {
      this.conversasDaPasta = {}
    },
    // Quem recarrega as duas listas (pastas + conversas) é o helper — a cópia
    // local recarregava as pastas sem `todosAgentes` e o chip mudava de universo.
    async deleteFolder(folder) {
      const r = await excluirPasta(folder, this.criterioDeRecarga)
      if (r && r.status === 200) {
        this.$delete(this.expandedMap, folder.id)
        this.invalidarCacheDePastas()
      }
      // `excluirPasta` devolve `null` para pasta sem id — snack em branco.
      if (r) this.$showSnack(r)
    },
    // 250ms: curto o bastante para não parecer travado, longo o bastante para
    // não refiltrar a cada tecla.
    onBuscaInput(valor) {
      this.buscaDigitada = valor || ''
      if (this.buscaTimer) clearTimeout(this.buscaTimer)
      this.buscaTimer = setTimeout(() => {
        this.busca = this.buscaDigitada
        this.buscaTimer = null
      }, DEBOUNCE_BUSCA_MS)
    },
    toggleFolder(folderId) {
      const abrindo = !this.expandedMap[folderId]
      this.$set(this.expandedMap, folderId, abrindo)
      if (abrindo) this.carregarConversasDaPasta(folderId)
    },
    // Uma vez por pasta: reabrir não refaz a chamada. Falha degrada para o
    // agrupamento das recentes, que é o comportamento de antes.
    async carregarConversasDaPasta(folderId) {
      if (!folderId) return
      if (this.conversasDaPasta[folderId]) return
      if (this.pastasCarregando[folderId]) return
      this.$set(this.pastasCarregando, folderId, true)
      try {
        const r = await apiConversations.getConversations({
          limit: CONVERSATIONS_LIMIT,
          idFolder: folderId,
          todosAgentes: true,
        })
        if (r && r.status === 200) {
          this.$set(this.conversasDaPasta, folderId, r.value || [])
        }
      } catch (erro) {
        // ★ [21/09/2026] `catch` obrigatório, e não decorativo: `toggleFolder`
        // chama este método SEM `await` (é um handler de clique — abrir a pasta
        // não pode esperar a rede). Sem `catch`, uma falha de rede vira uma
        // PROMISE REJEITADA ÓRFÃ: no browser é `unhandledrejection`, e no Node
        // é `triggerUncaughtException` — foi isso que derrubava o processo
        // inteiro do Jest neste arquivo, fazendo a suíte morrer sem reportar
        // FAIL nenhum (os casos passavam isolados e a suíte inteira crashava).
        //
        // O `finally` sozinho NÃO basta: ele solta o loading e deixa a rejeição
        // seguir viagem. Degradar em silêncio aqui é o comportamento certo — a
        // promessa deste método é "uma vez por pasta, e falha volta para o
        // agrupamento das recentes", que é o que o `finally` abaixo preserva.
        // O erro vai ao console para não sumir de vez.
        // eslint-disable-next-line no-console
        console.warn('[IA] conversas da pasta não carregaram:', folderId, erro)
      } finally {
        this.$set(this.pastasCarregando, folderId, false)
      }
    },
    onLooseDragOver(event) {
      if (event) event.preventDefault()
      this.looseDragOver = true
    },
    onLooseDragLeave() {
      this.looseDragOver = false
    },
    onLooseDrop(event) {
      if (event) event.preventDefault()
      this.looseDragOver = false
      const payload = readConversationMovePayload(
        event ? event.dataTransfer : null
      )
      if (!payload || !payload.conversationId) return
      // Ja esta sem pasta → no-op (sem request).
      if (!payload.currentFolderId) return
      this.moveConversation({
        conversationId: payload.conversationId,
        folderId: null,
      })
    },
    async moveConversation({ conversationId, folderId }) {
      if (this.moving || !conversationId) return
      this.moving = true
      try {
        const r = await moverConversaParaPasta({
          conversationId,
          folderId,
          ...this.criterioDeRecarga,
        })
        if (r && r.status === 200) this.invalidarCacheDePastas()
        if (r) this.$showSnack(r)
      } finally {
        this.moving = false
      }
    },
  },
}
</script>

<template>
  <div class="conversations">
    <div
      class="conversations-header d-flex align-center justify-space-between pt-3 px-3 pb-2"
    >
      <span class="conversations-title az-ds-card-title">Conversas</span>
      <div class="d-flex align-center">
        <v-btn
          icon
          small
          aria-label="Nova pasta"
          title="Nova pasta"
          @click="openCreateFolder"
        >
          <icon-svg name="folder-add-outline" :size="18" color="#8E8E8E" />
        </v-btn>
        <v-btn
          icon
          small
          aria-label="Nova conversa"
          title="Nova conversa"
          @click="$emit('new-conversation')"
        >
          <icon-svg
            name="mdi-message-plus-outline"
            :size="18"
            color="#8E8E8E"
          />
        </v-btn>
      </div>
    </div>

    <!-- Busca client-side por título (spec-007 R7). Filtra a lista inteira;
         em busca ativa a árvore dá lugar aos resultados planos. -->
    <div class="conversations-busca px-3 pt-2 pb-1">
      <input-by-type
        type="search"
        :value="buscaDigitada"
        placeholder="Buscar conversas"
        max-width="100%"
        class="ia-sidebar-busca conversations-busca-campo"
        @input="onBuscaInput"
      />
    </div>

    <!-- Loading skeleton. `v-skeleton-loader` NÃO existe no Vuetify 2.0.11 deste
         projeto: `BaseLoadingSkeleton` é o componente do próprio DS. -->
    <div v-if="initialLoading" class="conversations-skeleton pa-2">
      <BaseLoadingSkeleton v-for="i in 3" :key="i" height="40px" class="mb-1" />
    </div>

    <!-- Falha de carga: distinta do vazio. Sem este estado, "não carregou" e
         "não existe" ficam indistinguíveis na tela (T-9c). -->
    <div
      v-else-if="mostraErro"
      class="conversations-erro text-center px-3 py-6"
    >
      <icon-svg name="info-circle" :size="18" color="secondary" class="mb-2" />
      <div class="az-text-secondary az-size-12 az-muted mb-3">
        Não foi possível carregar suas conversas. Verifique sua conexão e tente
        novamente.
      </div>
      <div class="d-flex justify-center">
        <BaseButton underlined min-width="80px" @click="$emit('retry')">
          Tentar de novo
        </BaseButton>
      </div>
    </div>

    <!-- Lista vazia -->
    <div
      v-else-if="mostraVazio"
      class="conversations-empty conversations-vazio text-caption text-center py-6"
    >
      Nenhuma conversa ainda
    </div>

    <!-- Pastas + conversas soltas -->
    <div v-else class="conversations-list py-2 px-2">
      <!-- Resultados da busca: lista plana, sem árvore (spec-007 R7) -->
      <template v-if="emBusca">
        <div
          v-if="!resultadosDaBusca.length"
          class="conversations-empty text-caption text-center py-4"
        >
          Nenhuma conversa encontrada
        </div>
        <ConversationRow
          v-for="conv in resultadosDaBusca"
          :key="'busca-' + conv.id"
          mostrar-agente
          :conversation="conv"
          :selected="conv.id === selectedId"
          :folders="folders"
          @select="onSelect"
          @rename="onRename"
          @request-delete="confirmDelete"
          @move="moveConversation"
        />
      </template>

      <!-- RECENTES: as últimas conversas em lista plana, com teto de 5 e
           "Ver todas (N)" (spec-007 R7 — desenho do protótipo v0). -->
      <template v-if="!emBusca">
        <button
          class="conversations-secao-header"
          :aria-expanded="secoes.recentes ? 'true' : 'false'"
          @click="alternarSecao('recentes')"
        >
          <span class="conversations-secao-titulo az-ds-label">Recentes</span>
          <icon-svg
            name="arrow-down-light"
            :size="12"
            color="var(--neutral-400)"
            class="conversations-secao-chevron"
            :class="{ 'conversations-secao-chevron--fechada': !secoes.recentes }"
          />
        </button>
        <template v-if="secoes.recentes">
          <ConversationRow
            v-for="conv in recentes.visiveis"
            :key="'recente-' + conv.id"
            mostrar-agente
          :conversation="conv"
            :selected="conv.id === selectedId"
            :folders="folders"
            @select="onSelect"
            @rename="onRename"
            @request-delete="confirmDelete"
            @move="moveConversation"
          />
          <button
            v-if="recentes.temMais"
            class="conversations-ver-todas ia-texto-auxiliar"
            :aria-expanded="recentesExpandido ? 'true' : 'false'"
            @click="alternarRecentes"
          >
            {{ recentesExpandido ? 'Ver menos' : 'Ver todas (' + recentes.total + ')' }}
          </button>
        </template>

        <button
          class="conversations-secao-header mt-2"
          :aria-expanded="secoes.pastas ? 'true' : 'false'"
          @click="alternarSecao('pastas')"
        >
          <span class="conversations-secao-titulo az-ds-label">Pastas</span>
          <icon-svg
            name="arrow-down-light"
            :size="12"
            color="var(--neutral-400)"
            class="conversations-secao-chevron"
            :class="{ 'conversations-secao-chevron--fechada': !secoes.pastas }"
          />
        </button>
      </template>

      <!-- Bloco de pastas -->
      <template v-if="!emBusca && secoes.pastas && hasFolders">
        <div
          v-for="folder in folders"
          :key="folder.id"
          class="conversations-folder"
        >
          <FolderItem
            :folder="folder"
            :count="folder.conversation_count || 0"
            :expanded="!!expandedMap[folder.id]"
            @toggle="toggleFolder"
            @rename="openRenameFolder"
            @delete="confirmDeleteFolder"
            @drop-conversation="moveConversation"
          />
          <div
            v-if="expandedMap[folder.id]"
            class="conversations-folder__children pl-4"
          >
            <ConversationRow
              v-for="conv in conversasPorPasta[folder.id] || []"
              :key="conv.id"
              mostrar-agente
          :conversation="conv"
              :selected="conv.id === selectedId"
              :folders="folders"
              @select="onSelect"
              @rename="onRename"
              @request-delete="confirmDelete"
              @move="moveConversation"
            />
            <!-- `pastasCarregando` era escrito e nunca chegava ao template: ao
                 expandir, o gestor lia "Nenhuma conversa nesta pasta" durante
                 TODA a requisição e concluía que a pasta estava vazia. -->
            <div
              v-if="pastasCarregando[folder.id]"
              class="conversations-folder__empty az-text-small text-center py-2 px-3"
            >
              Carregando…
            </div>
            <div
              v-else-if="!(conversasPorPasta[folder.id] || []).length"
              class="conversations-folder__empty az-text-small text-center py-2 px-3"
            >
              Nenhuma conversa nesta pasta
            </div>
          </div>
        </div>
      </template>

      <!-- Bloco de conversas soltas — visível mesmo vazio quando há pastas,
           pra sempre existir um drop-target ao arrastar pra fora da pasta -->
      <div
        v-if="!emBusca && secoes.pastas && (hasFolders || hasLoose)"
        class="conversations-loose mt-1"
        :class="{ 'conversations-loose--drag-over': looseDragOver }"
        @dragover="onLooseDragOver"
        @dragleave="onLooseDragLeave"
        @drop="onLooseDrop"
      >
        <div
          class="conversations-loose__header d-flex align-center az-gap-small pt-2 pb-1 px-3"
        >
          <span class="az-ds-label">Sem pasta</span>
          <span class="az-ds-badge-count az-neutral ml-2">
            {{ looseConversations.length }}
          </span>
        </div>
        <ConversationRow
          v-for="conv in looseConversations"
          :key="conv.id"
          mostrar-agente
          :conversation="conv"
          :selected="conv.id === selectedId"
          :folders="folders"
          @select="onSelect"
          @rename="onRename"
          @request-delete="confirmDelete"
          @move="moveConversation"
        />
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.conversations
  display: flex
  flex-direction: column
  height: 100%

.conversations-header
  border-bottom: 1px solid var(--border-hairline)
  flex-shrink: 0

.conversations-list
  flex: 1
  overflow-y: auto

.conversations-empty
  color: var(--neutral-600)

.conversations-busca
  flex-shrink: 0

// A APARÊNCIA do campo vem de `.ia-sidebar-busca` (global, em
// `design/_ia-sidebar.sass`) — a mesma da busca da página cheia. Havia uma
// cópia aqui, e ela divergiu: este comentário afirmava usar "a mesma receita"
// da sidebar enquanto os dois fundos eram diferentes, e o `background-color`
// daqui nem chegava a valer (sem `!important`, perdia para a regra do
// Vuetify). Aqui fica só a LARGURA, que é do contexto: a busca acompanha a
// lista, não é campo isolado de formulário.
.conversations-busca-campo
  width: 100%

// RECENTES / PASTAS são a âncora de leitura da lista: `az-ds-label` os deixa em
// 600/#71717A, que some contra os títulos das conversas logo abaixo.
.conversations-secao-titulo
  font-weight: 700
  color: var(--neutral-700)

.conversations-secao-header
  display: flex
  align-items: center
  justify-content: space-between
  width: 100%
  background: none
  border: none
  cursor: pointer
  padding: 6px 8px
  border-radius: var(--radius-sm)

  &:hover
    background: var(--neutral-50)

.conversations-secao-chevron
  transition: transform var(--motion-duration-default) var(--motion-easing-standard)

  // Recolhido aponta para CIMA (a seção subiu), não para o lado: de lado ele
  // lê como "abre para a direita", que não é o que o clique faz.
  &--fechada
    transform: rotate(180deg)

// Estilo de link (sem cromo de botão, sublinha no hover), mas na cor da lista.
// Com `az-ds-hyperlink` ele vinha em `--primary` e virava o elemento mais
// chamativo do histórico — competindo com os títulos das conversas, que são o
// conteúdo. É um atalho para o resto da lista, não uma chamada para ação.
//
// `font-size` explícito, não `var(--font-size-xs)`: em `src/design/_vars.sass:89`
// a custom property é declarada como `--font-size-xs: $font-size-xs`, SEM a
// interpolação `#{}` — o valor vira a string literal `$font-size-xs`, que é CSS
// inválido, e o `var()` cai no herdado (16px). Vale para toda a família
// `--font-size-*`/`--font-weight-*`; corrigir a partial é outro PR, porque
// encolheria texto em financial-planning e tarefas de uma vez.
.conversations-ver-todas
  color: var(--neutral-600)
  display: block
  width: 100%
  background: none
  border: none
  padding: 6px 8px
  cursor: pointer
  text-align: left

  &:hover
    text-decoration: underline
    color: var(--neutral-800)

.conversations-loose
  border-radius: var(--radius-sm)
  transition: background 0.12s

  &--drag-over
    background: var(--neutral-100)
    outline: 2px dashed var(--primary)
    outline-offset: -2px
</style>
